/**
 * @file 组件缓存相关函数实现
 */
import { parseFunction, parseParam, replaceDynamicParameter } from '../index';
import { ShapeConstants } from '../../penpot/constant';
import { getWebComponents } from '../../../api/webComponents';
import { DYNAMIC_PARAMETER_TYPE, PARAMETER_TYPE } from '../constant';
import {
  addRenderComponents,
  deleteRenderComponentById,
  getConfigByInstanceId,
  getRenderComponentById,
  getRenderComponentsById,
  getRenderDataSource,
  setRenderComponentById,
  setRenderComponents,
  updateRenderComponentAttr,
} from '../../../store/common/action';
import { deletePureChild, getAllChildPropsWithSelf } from '../../atomicCompDataHandleFun/initViewComp';
import { mergeObj2CompAttrs, uuid4 } from '../../index';
import { genStyleTag } from '../../tailwindcss';
import {
  assembleWebComponentTree,
  isEmptyObject,
  parseWebComponentTree,
  PROP_SUFFIX_CONNECTOR,
  recursionCustomParams,
} from '../../common';
import { slot } from '../../../constant/WebComponentsConstants';
import {triggerBeforeLoad} from "../../hookFunUtil";

/**
 * 获取组件缓存AST
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const getComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    let componentProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    return getComponentImpl(componentProp, instanceId);
  } else {
    return undefined;
  }
};

/**
 * 设置组件属性值
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const setAttr = async (func, customParamsList, customFunctionParams, instanceId) => {
  console.log('setAttr 入参--------------', func, customParamsList, customFunctionParams, instanceId);
  if (_size(_get(func, 'inParams')) === 3) {
    let componentProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    let attr = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    let value = await parseParam(_get(func, 'inParams')[2], customParamsList, customFunctionParams, instanceId);
    console.log('setAttr----------', componentProp, attr, value, instanceId);
    await setAttrImpl(componentProp, attr, value, instanceId);
  }
};

/**
 * 获取组件属性路径
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const getAttrUrlFun = async (func, customParamsList, customFunctionParams, instanceId) => {
  console.log('getAttrUrl-----', func, customParamsList, customFunctionParams, instanceId);
  if (_size(_get(func, 'inParams')) === 1) {
    return _get(func, ['inParams', 0, 'inParams', 0, 'value']);
  }
};


/**
 * 获取循环区域循环次数
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const getFrequency = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    let cyclicRegionProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    let component = getRenderComponentById(instanceId, cyclicRegionProp);
    if (_get(component, 'attrs.cycleMode.value') === 'followNormalArray') {
      //跟随普通入参
      return _size(_get(component, 'attrs.dataList.value', []));
    } else {
      //固定循环次数
      return _get(component, 'attrs.frequency.value', 0);
    }
  }
};


/**
 * 递归删除循环区域子节点
 * @param cyclicRegionProp
 * @param componentsMap
 */
export const removeChildren = async (cyclicRegionProp, componentsMap) => {
  console.log('removeChildren------:', componentsMap);
  // 获取到循环区域节点
  const cyclicRegion = componentsMap._get(cyclicRegionProp);
  if (!cyclicRegion) {
    return;
  }
  for (const data of [cyclicRegion]) {
    const component = componentsMap._get(data.prop);
    if (component) {
      componentsMap.delete(data.prop);
      console.log('Removed cyclic region:', data);
    }
    if (cyclicRegion.children && cyclicRegion.children.length > 0) {
      cyclicRegion.children.forEach(child => {
        removeChildren(child.prop, componentsMap);
      });
    }
  }

};


/**
 * 查找节点的直接亲属节点（祖先节点或子节点）
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const findRelativeNode = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 2) {
    let nodeProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    let relativeProp = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    const components = getRenderComponentsById(instanceId);
    return findChildrenNode(components, nodeProp, relativeProp) || findParentNode(components, nodeProp, relativeProp) || findPeerNode(components, nodeProp, relativeProp);
  }
};


/**
 * 查找节点的直接父节点
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const findComponentParentNode = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    let node = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    return getRenderComponentById(instanceId, node.parent);
  }
};


/**
 * 根据当前组件对象，向下查找其指定层级的子组件。
 * (这里假设你的组件树是通过 ID 和 children 数组构建的)
 *
 * @returns {Promise<Object|null>} 返回找到的子组件对象，如果找不到则返回 null。
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const findComponentChildNode = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 2) {
    let currentNode = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    let childIndex = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    // 校验子节点列表是否存在且为数组
    const children = currentNode.children;
    if (!Array.isArray(children)) {
      console.warn('findComponentChildNode: 当前节点的 "children" 属性不是一个数组。');
      return null;
    }

    // 校验索引是否为有效范围的正整数
    if (!Number.isInteger(childIndex) || childIndex < 0 || childIndex >= children.length) {
      console.warn(`findComponentChildNode: 子节点索引 ${childIndex} 超出范围或不是非负整数。子节点总数为 ${children.length}。`);
      return null;
    }
    return getRenderComponentById(instanceId, children[childIndex].prop);
  }
};
/**
 * 查找节点的直接亲属节点（祖先节点或子节点）并返回节点prop
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const findRelativeNodeProp = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 2) {
    let nodeProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    let relativeProp = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    const components = getRenderComponentsById(instanceId);
    let relativeNode = findChildrenNode(components, nodeProp, relativeProp) || findParentNode(components, nodeProp, relativeProp) || findPeerNode(components, nodeProp, relativeProp);
    return _get(relativeNode, 'prop', '');
  }
};

const findChildrenNode = (components, nodeProp, relativeProp) => {
  let component = components.get(nodeProp);
  let childrenNode = null;
  if (_size(component.children) === 0) {
    return null;
  } else {
    let relativeNode = component.children.find(item => {
      return item.prop.indexOf(relativeProp) !== -1;
    });
    if (relativeNode) {
      return components.get(relativeNode.prop);
    } else {
      for (let child of component.children) {
        childrenNode = childrenNode || findChildrenNode(components, child.prop, relativeProp);
      }
      return childrenNode;
    }
  }
};

const findParentNode = (components, nodeProp, relativeProp) => {
  let component = components.get(nodeProp);
  if (!component.parent) {
    return null;
  } else {
    if (component.parent.indexOf(relativeProp) !== -1) {
      return components.get(component.parent);
    } else {
      return findParentNode(components, component.parent, relativeProp);
    }
  }
};

const findPeerNode = (components, nodeProp, relativeProp) => {
  let component = components.get(nodeProp);
  if (!component.parent) {
    return null;
  } else {
    let result;
    for (const [item] of components.entries()) {
      if (item.parent === component.parent && item.prop.includes(relativeProp)) {
        result = item; // 找到匹配项
        break; // 如果只需要第一个匹配项，可以提前终止
      }
    }
    return result;
  }
};

export const getComponentImpl = (componentProp, instanceId) => {
  const components = getRenderComponentsById(instanceId);
  if (!components) {
    return;
  }
  return components.get(componentProp);
};

export const findParent = (componentProp, instanceId) => {
  const components = getRenderComponentsById(instanceId);
  return components.get(componentProp);
};

export const getAttrImpl = (componentProp, attr, instanceId) => {
  let component = getComponentImpl(componentProp, instanceId);
  if (component) {
    console.log('获取属性值', _get(component, `attrs.${attr}.value`));
    return _get(component, `attrs.${attr}.value`);
  } else {
    console.log('获取属性值失败', attr, componentProp);
    return '';
  }
};

export const setAttrImpl = async (componentProp, attr, value, instanceId) => {
  // console.log('setAttrImpl--->>', componentProp, attr, value, instanceId);
  let component = getComponentImpl(componentProp, instanceId);
  // console.log('component-----', component);
  if (component) {
    // 判断值类型，如果存在解构场景，则解构
    if (_get(component, `attrs.${attr}.type`) == 'destructuring') {
      value = _get(value, _get(component, `attrs.${attr}.destructuringPath`), value);
    }
    await updateRenderComponentAttr(instanceId, component, attr, value);
  }
};

/**
 *
 * @param componentProp
 * @param attr
 * @param currentItem
 * @param value
 * @param instanceId
 * @returns
 */
export const setExpression = async (componentProp, attr, currentItem, value, instanceId) => {
  let component = getComponentImpl(componentProp, instanceId);
  const attrValue = _get(component, attr, componentProp);
  let filtedCurrentItem = currentItem.filter(item => item.paramType === PARAMETER_TYPE.OPERATOR);
  // console.log("attrValue",attrValue);
  // console.log("filtedCurrentItem",filtedCurrentItem);
  filtedCurrentItem = [{
    paramType: PARAMETER_TYPE.FIXED,
    value: attrValue,
  },
    ...filtedCurrentItem,
    {
      paramType: PARAMETER_TYPE.FIXED,
      value: value,
    }];
  // console.log('filtedCurrentItem------------', filtedCurrentItem);
  return filtedCurrentItem;
};


/**
 * 组件及其子组件平移
 * @param prop
 * @param offset
 * @param webComponentsAST
 */
export const translateComponent = (prop, offset, webComponentsAST) => {
  let component = webComponentsAST.components.find(item => {
    return item.prop === prop;
  });
  let { offsetX, offsetY } = offset;
  component.css[ShapeConstants.x] += offsetX;
  component.css[ShapeConstants.y] += offsetY;
  //平移子组件
  if (_size(component.children) > 0) {
    component.children.forEach(child => {
      translateComponent(child.prop, offset, webComponentsAST);
    });
  }
};


/**
 * 追加子组件，取当前组件的第一个儿子顺位追加，没有儿子则直接追加
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const addSubComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  // 参数列表
  let inParams = _get(func, 'inParams');
  let inParamsSize = _size(inParams);
  if (inParamsSize == 0) {
    // 消除无参副作用
    return {};
  }
  let compProp, templateId, value;
  // 组件prop
  compProp = await parseParam(inParams[0], customParamsList, customFunctionParams, instanceId);
  if (compProp && inParamsSize > 1) {
    templateId = await parseParam(inParams[1], customParamsList, customFunctionParams, instanceId);
  }
  if (templateId && typeof templateId == 'string') {
    let { head, body } = await getNewTemplateComp(templateId);
    // 填值
    if (inParamsSize > 1) {
      value = await parseParam(inParams[2], customParamsList, customFunctionParams, instanceId);
    }
    // 对象映射
    if (value) {
      mergeObj2CompAttrs(head, value);
    }

    // 重建当前实例
    let renderComp = getRenderComponentById(instanceId, compProp);
    // 设置父子关系
    head['parent'] = compProp;
    renderComp.children.push({ prop: head['prop'] });
    body.push(head);
    body.unshift(renderComp);
    // 重新渲染
    const board = setRenderComponents(instanceId, body);
    await triggerBeforeLoad(body, instanceId);
    setRenderComponentById(instanceId, board.prop, board);
    // 返回头供后续处理
    return head;
  }
  return {};
};

export const addContentToComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  // 追加内容到组件，参数1：prop；参数2：属性；参数3：内容
  let inParams = _get(func, 'inParams');
  let inParamsSize = _size(inParams);
  if (inParamsSize == 0 || inParamsSize !== 3) {
    // 消除无参副作用
    return {};
  }
  const content = await parseParam(inParams[2], customParamsList, customFunctionParams, instanceId);
  if (content == null) {
    return {};
  }
  // 组件prop
  const comp = await parseParam(inParams[0], customParamsList, customFunctionParams, instanceId);
  const attrs = _cloneDeep(inParams[1]);
  // 获取key
  const matchKey = attrs.inParams[0].value;
  const targetKey = matchKey.match(/^attrs\.(.+)\.value$/)?.[1];
  if (targetKey == null) {
    return {};
  }
  attrs.inParams.unshift(inParams[0]);
  let targetValue = await parseParam(attrs, customParamsList, customFunctionParams, instanceId);
  // 赋值
  if (targetValue == null) {
    targetValue = content;
  } else if (['number', 'string', 'bigint'].includes(typeof targetValue)) {
    targetValue += content;
  } else if (typeof targetValue === 'boolean') {
    targetValue = content;
  } else if (Array.isArray(targetValue)) {
    targetValue.push(content);
  } else if (typeof targetValue === 'object') {
    Object.assign(targetValue, content);
  }
  await setAttrImpl(comp.prop, targetKey, targetValue, instanceId);
  return targetValue;
};

export const addPageUIComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  let compProp, data;
  let inParams = _get(func, 'inParams');
  let inParamsSize = _size(inParams);
  if (inParamsSize == 0) {
    // 消除无参副作用
    return {};
  }
  // 组件prop
  compProp = await parseParam(inParams[0], customParamsList, customFunctionParams, instanceId);
  if (compProp && inParamsSize > 1) {
    data = await parseParam(inParams[1], customParamsList, customFunctionParams, instanceId);
  }
  if (data && _size(_get(data, ['content'])) > 0) {
    for (const page of _get(data, ['content'])) {
      const { head, body } = await replaceCompNode(page);
      console.log('head-----', head);
      console.log('body-----', body);
      const renderComp = getRenderComponentById(instanceId, compProp);
      // 设置父子关系
      head['parent'] = compProp;
      renderComp.children.push({ prop: head['prop'] });
      body.push(head);
      body.unshift(renderComp);
      // 重新渲染
      const board = setRenderComponents(instanceId, body);
      setRenderComponentById(instanceId, board.prop, board);
    }
  }
};


/**
 * 组件root节点替换成插槽
 * @param pageComponents
 */
const replaceCompNode = async (pageComponents) => {
  let head = slot(), body = [];
  const components = pageComponents.components;
  for (const comp of components) {
    if (comp.prop === 'root') {
      head.dynamicCss = comp.dynamicCss;
      head.event = comp.event;
      head.hook = comp.hook;
      break;
    }
  }
  _set(pageComponents, ['components'], components);
  _set(head, ['attrs', 'components', 'value'], pageComponents);
  return { head, body };
};


export async function attrWatchHandler(component, attr, instanceId) {
  //触发组件属性的change事件
  if (_get(component, `attrs.${attr}.triggerSetting.functionBody.length`, 0) > 0) {
    let triggerSetting = _cloneDeep(_get(component, `attrs.${attr}.triggerSetting`));
    const customParamMap = recursionCustomParams(triggerSetting.functionBody, {});
    const customParamsList = Object.values(customParamMap);
    replaceDynamicParameter(customParamsList, DYNAMIC_PARAMETER_TYPE.CURRENT_PROP, component.prop);
    replaceDynamicParameter(customParamsList, DYNAMIC_PARAMETER_TYPE.CURRENT_COMPONENT, component);
    await parseFunction(triggerSetting.functionBody, customParamsList, [], instanceId);
  }
}


// 使用临时变量缓存多次执行结果
let cacheComp = {};

/**
 * 查找模板组件，并去除root节点
 * @param templateId
 */
const getNewTemplateComp = async (templateId) => {
  if (!cacheComp[templateId]) {
    const query = { 'page': 1, 'limit': 1, 'domainModelCode': 'common', 'params': { _id: templateId } };
    const response = await getWebComponents(query);
    cacheComp[templateId] = response['data'].items[0];
    // 设置样式
    genStyleTag(cacheComp[templateId]['tailwindStyleCodes']);
    delete cacheComp[templateId]['tailwindStyleCodes'];
  }
  return getNewComponent(_cloneDeep(cacheComp[templateId]['components']));
};

/**
 * 刷新模板组件的prop
 * @param data
 */
const getNewComponent = (data) => {
  let head = {}, body = [];
  // 查找最外层组件
  let firstProp = '';
  for (const comp of data) {
    if (comp.prop === 'root' && comp.children && comp.children.length > 0) {
      firstProp = comp.children[0]['prop'];
    }
  }
  for (const comp of data) {
    // 头身分离
    const newProp = uuid4();
    if (comp.prop != 'root' && comp.prop != firstProp) {
      replaceProp(data, comp.prop, newProp);
      body.push(data.find(item => item.prop === newProp));
    } else if (comp.prop == firstProp) {
      replaceProp(data, comp.prop, newProp);
      head = data.find(item => item.prop == newProp);
    }
  }
  return { head, body };
};

const replaceProp = (data, oldProp, newProp) => {
  for (let i = 0; i < data.length; i++) {
    // 将每个对象深拷贝为字符串
    let event = JSON.stringify(data[i]['event']);
    let hook = JSON.stringify(data[i]['hook']);
    let compProp = data[i].prop;
    let compParent = data[i].parent;
    let compChildren = JSON.stringify(data[i].children);
    // 使用正则批量替换字符串中的值
    data[i].event = JSON.parse(event.replace(new RegExp(oldProp, 'g'), newProp));
    data[i].hook = JSON.parse(hook.replace(new RegExp(oldProp, 'g'), newProp));
    data[i].prop = compProp.replace(new RegExp(oldProp, 'g'), newProp);
    data[i].parent = compParent.replace(new RegExp(oldProp, 'g'), newProp);
    data[i].children = JSON.parse(compChildren.replace(new RegExp(oldProp, 'g'), newProp));
  }
};

/**
 * 获取实例对象函数（例如：获取弹框页面中的填写的表单数据）
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const getExampleObjectFun = async (func, customParamsList, customFunctionParams, instanceId) => {
  const len = _size(_get(func, 'inParams'));
  if (len <= 2) {
    let instance = instanceId;
    let componentProp = '';
    const resultObject = {};
    if (instance.includes('__')) {
      instance = getParentPath(instance, instanceId);
    }
    if (len === 1) {
      componentProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    } else {
      instance = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
      componentProp = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    }
    if (componentProp) {
      const component = getRenderComponentById(instance, componentProp);
      Object.keys(component.attrs).forEach(key => {
        resultObject[key] = component.attrs[key].value;
      });
    } else {
      const renderMap = getRenderComponentsById(instance);
      if (renderMap) {
        for (const component of renderMap.values()) {
          if (component && component.attrs) {
            for (const key in component.attrs) {
              if (component.attrs.hasOwnProperty(key)) {
                if (key.startsWith('p')) {
                  resultObject[key] = component.attrs[key].value;
                }
              }
            }
          }
        }
      }
    }
    return resultObject;
  }
};


/**
 * 设置实例对象函数（例如：回写数据到弹框页面中表单中）
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const setExampleObjectFun = async (func, customParamsList, customFunctionParams, instanceId) => {
  const len = _size(_get(func, 'inParams'));
  if (len <= 3) {
    let instance = '';
    let componentProp = '';
    let component: any = {};
    let paramData = {};
    instance = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);// 回填表单的数据
    // 传实例id场景
    if (typeof instance !== 'object') {
      paramData = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
      // 不能是空对象
      if (isEmptyObject(paramData)) {
        return;
      }
      componentProp = await parseParam(_get(func, 'inParams')[2], customParamsList, customFunctionParams, instanceId);
    } else {
      // 没有传实例场景
      instance = instanceId;
      if (instance.includes('__')) {
        instance = getParentPath(instance, instanceId);
      }
      paramData = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
      componentProp = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    }
    if (componentProp) {
      component = _cloneDeep(getRenderComponentById(instance, componentProp));
      for (const key in component.attrs) {
        if (paramData[key]) {
          _set(component, `attrs.${key}.value`, paramData[key]);
        }
      }
      setRenderComponentById(instance, component.prop, component);
    } else {
      const renderMap = getRenderComponentsById(instance);
      if (renderMap) {
        for (const component of renderMap.values()) {
          if (component && component.attrs) {
            let hasChange = false;
            for (const key in component.attrs) {
              if (component.attrs.hasOwnProperty(key)) {
                if (key.startsWith('p')) {
                  // 判断字段是否存在
                  if (paramData.hasOwnProperty(key)) {
                    _set(component, key, paramData[key]);
                    _set(component, 'textValue', paramData[key]);
                    hasChange = true;
                  }
                }
              }
            }
            if (hasChange) {
              setRenderComponentById(instance, component.prop, component);
            }
          }
        }
      }
    }
  }
};


/**
 * 去掉最后一段，返回剩余路径
 * 'A__B'       → 'A'
 * 'A__B__C'    → 'A__B'
 * 'A__B__C__D' → 'A__B__C'
 */
const getParentPath = (instanceStr, fallback) => {
  if (typeof instanceStr !== 'string') return fallback;
  const parts = instanceStr.split('__');
  if (parts.length < 2) return fallback;

  return parts.slice(0, -1).join('__');
};

/**
 * 组件属性映射
 * @param source
 * @param target
 * @returns
 */
// const mapObject = (source, target) => {
//   const result = {...source};
//
//   for (const key in target) {
//     if (source.hasOwnProperty(key) &&
//       typeof target[key] === 'object' &&
//       target[key] !== null &&
//       target[key].default === true) {
//
//       // 只更新 value 字段，保留其他字段不变
//       result[key] = {
//         ...result[key],
//         value: target[key].value
//       };
//     }
//   }
//
//   return result;
// }

/**
 * 清空指定节点下所有的子
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const clearChildren = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    const componentProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    const renderTreeMap = getRenderComponentsById(instanceId);
    const component = getRenderComponentById(instanceId, componentProp);
    // 清空指定组件下所有的子
    deletePureChild(renderTreeMap, component.prop, instanceId);
    component.children = [];
    // 清空所有子后刷新当前组件
    setRenderComponentById(instanceId, component.prop, component);
  }

};

/**
 * 获取指定数据源的数据总数
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const getDataSourceTotalFun = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    const dataSource = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    const data = getRenderDataSource(instanceId, dataSource);
    return _get(data, ['paging', 'total']);
  }
};


/**
 * 拷贝指定组件到指定容器下
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const addElementComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 2 || _size(_get(func, 'inParams')) === 3 || _size(_get(func, 'inParams')) === 4) {
    let body = [];
    let attrsObject = {};
    let dataIndex = 0;
    const componentProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    const targetProp = await parseParam(_get(func, 'inParams')[1], customParamsList, customFunctionParams, instanceId);
    const targetComponent = getRenderComponentById(instanceId, targetProp);
    const designConfig = getConfigByInstanceId(instanceId);
    const boardConfig = designConfig.components.find(item => {
      return item.prop === componentProp;
    });
    if (boardConfig) {
      const root = {};
      assembleWebComponentTree(designConfig.components, boardConfig.prop, root);
      const compList = [];
      parseWebComponentTree([root], compList);
      if (_size(compList) > 0) {
        for (const comp of compList) {
          const newProp = `${comp.prop}${PROP_SUFFIX_CONNECTOR}${uuid4()}`;
          replaceProp(compList, comp.prop, newProp);
          body.push(compList.find(item => item.prop === newProp));
        }
      }
      if (_size(_get(func, 'inParams')) === 3 || _size(_get(func, 'inParams')) === 4) {
        attrsObject = await parseParam(_get(func, 'inParams')[2], customParamsList, customFunctionParams, instanceId);
        for (const data of body) {
          for (const key in data.attrs) {
            if (attrsObject[key]) {
              data.attrs[key].value = attrsObject[key];
            }
          }
          if (_size(_get(func, 'inParams')) === 4) {
            dataIndex = await parseParam(_get(func, 'inParams')[3], customParamsList, customFunctionParams, instanceId);
            console.log('dataIndex-----------', dataIndex);
            data.dataIndex = dataIndex;
          }
        }
      }
      console.log('addElementComponent------', body);
      addRenderComponents(instanceId, body);
      if (targetComponent) {
        targetComponent['children'].push({ prop: body[0].prop });
        setRenderComponentById(instanceId, targetComponent['prop'], targetComponent);
      }
    }
    return targetComponent;
  }
};


/**
 * 删除指定组件包括自己
 * @param func
 * @param customParamsList
 * @param customFunctionParams
 * @param instanceId
 */
export const delElementComponent = async (func, customParamsList, customFunctionParams, instanceId) => {
  if (_size(_get(func, 'inParams')) === 1) {
    const triggerProp = await parseParam(_get(func, 'inParams')[0], customParamsList, customFunctionParams, instanceId);
    const renderTreeMap = getRenderComponentsById(instanceId);
    const component = getRenderComponentById(instanceId, triggerProp);
    const parentComp = getRenderComponentById(instanceId, component.parent);
    // 清空指定组件下所有的子
    const result = getAllChildPropsWithSelf(renderTreeMap, component.prop);
    if (result.length > 0) {
      result.forEach(childProp => {
        deleteRenderComponentById(instanceId, childProp);
      });
    }
    console.log(parentComp);
    const targetIndex = parentComp.children.findIndex(child => child.prop === component.prop);
    if (targetIndex > -1) {
      parentComp.children.splice(targetIndex, 1);
    }
    // 清空所有子后刷新当前组件
    setRenderComponentById(instanceId, parentComp.prop, parentComp);
    return parentComp;
  }
};
